/**
 * Network UI controller — fetches /network/instances from the gateway and
 * tracks the currently selected + compared ids.
 */

import type { InstanceComparison, InstanceRecord } from "../../../../src/network/types.ts";
import type { GatewayBrowserClient } from "../gateway.ts";

export type NetworkInstance = InstanceRecord & { status: "online" | "degraded" | "offline" };

export type NetworkState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  networkLoading: boolean;
  networkInstances: NetworkInstance[];
  networkError: string | null;
  networkStatus: string | null;
  networkSelectedId: string | null;
  networkComparingIds: string[];
  networkComparison: InstanceComparison | null;
  networkComparisonLoading: boolean;
  networkComparisonError: string | null;
};

export async function loadNetwork(state: NetworkState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  if (state.networkLoading) {
    return;
  }
  state.networkLoading = true;
  state.networkError = null;
  state.networkStatus = null;
  try {
    const res = (await state.client.request("network.list", {})) as {
      ok?: boolean;
      instances?: NetworkInstance[];
    };
    const list = Array.isArray(res?.instances) ? res.instances : [];
    state.networkInstances = list;
    state.networkStatus = list.length === 0 ? "No instances reported yet." : null;
  } catch (err) {
    state.networkError = err instanceof Error ? err.message : String(err);
    state.networkInstances = [];
  } finally {
    state.networkLoading = false;
  }
}

export async function loadNetworkComparison(state: NetworkState, ids: string[]): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  if (ids.length < 2) {
    state.networkComparison = null;
    return;
  }
  state.networkComparisonLoading = true;
  state.networkComparisonError = null;
  try {
    const res = (await state.client.request("network.diff", { ids })) as {
      ok?: boolean;
      comparison?: InstanceComparison;
    };
    state.networkComparison = res.comparison ?? null;
  } catch (err) {
    state.networkComparisonError = err instanceof Error ? err.message : String(err);
    state.networkComparison = null;
  } finally {
    state.networkComparisonLoading = false;
  }
}

export function toggleNetworkCompare(state: NetworkState, id: string): void {
  const next = new Set(state.networkComparingIds);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  state.networkComparingIds = [...next];
}

export function selectNetworkInstance(state: NetworkState, id: string | null): void {
  state.networkSelectedId = id === "" ? null : id;
}
